// SPDX-License-Identifier: MIT
// Custom AddDataModal to show 'upload' and 'storage' tabs with LoadSEER provider
import React from 'react';
import { LoadDataModalFactory as DefaultLoadDataModalFactory } from '@kepler.gl/components';
import ModalTabsFactory from '@kepler.gl/components/dist/modals/modal-tabs';
import FileUploadFactory from '@kepler.gl/components/dist/common/file-uploader/file-upload';
import LoadTilesetFactory from '@kepler.gl/components/dist/modals/tilesets-modals/load-tileset';
import { NodeBrowserPane } from '../integrations';
import { AzureProviderInterface } from '../cloud-providers';
import { env } from '../env.config';

// Direct TypeScript version of the kepler.gl example

import type { FC } from 'react';

// Fallback: Redefine LoadDataModalProps locally if not exported from kepler.gl
type LoadDataModalProps = {
  onFileUpload: (files: File[]) => void;
  onLoadCloudMap: (provider: unknown, vis: unknown) => void;
  onTilesetAdded: (
    tileset: { name: string; type: string; metadata: Record<string, unknown> },
    processedMetadata?: Record<string, unknown>
  ) => void;
  fileLoading: boolean;
  loadingMethods?: unknown[];
  fileFormatNames: string[];
  fileExtensions: string[];
  isCloudMapLoading: boolean;
  disableExtensionFilter?: boolean;
  onClose?: (...args: unknown[]) => unknown;
  loadFiles: unknown;
  fileLoadingProgress: unknown;
  cloudProviders?: unknown[];
  onSetCloudProvider?: (provider: unknown) => void;
  currentProvider?: string;
};

const CustomLoadDataModalFactory = () => {
  // Instantiate the default modal with all required dependencies
  const DefaultLoadDataModal = DefaultLoadDataModalFactory(
    ModalTabsFactory(),
    FileUploadFactory(),
    () => <NodeBrowserPane />,
    LoadTilesetFactory()
  );

  // Return a typed functional component (compute loading methods at render time so flag changes / overrides are respected before first render)
  const CustomLoadDataModal: FC<LoadDataModalProps> = props => {
    const loadingMethods = [
      {
        id: 'upload',
        label: 'modal.loadData.upload',
        elementType: FileUploadFactory(),
      },
      // Only show LoadSEER API tab if enabled
      ...(env.VITE_LOADSEER_API_ENABLED
        ? [
            {
              id: 'loadseer-api',
              label: 'LoadSEER API',
              elementType: () => <NodeBrowserPane />,
            },
          ]
        : []),
      {
        id: 'storage',
        label: 'LoadSEER Storage',
        elementType: () => <AzureProviderInterface />,
      },
    ];

    return <DefaultLoadDataModal {...(props as any)} loadingMethods={loadingMethods} />;
  };

  return CustomLoadDataModal;
};

// Export as a kepler.gl factory override
export const replaceLoadDataModal = () => [DefaultLoadDataModalFactory, CustomLoadDataModalFactory];
